import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import NetLoadVisual from "../../components/MarketMonitor/NetLoadVisual";
import LoadZoneActualLoadVisual from "../../components/MarketMonitor/LoadZoneActualLoadVisual";
import type { LoadZoneObjectName } from "../../components/MarketMonitor/LoadZoneActualLoadVisual";
import LoadZoneForecastVisual from "../../components/MarketMonitor/LoadZoneForecastVisual";
import LoadZoneGenerationSpreadVisual from "../../components/MarketMonitor/LoadZoneGenerationSpreadVisual";
import LoadZoneRenewableSummaryVisuals from "../../components/MarketMonitor/LoadZoneRenewableSummaryVisuals";
import { matchesSelectedIso } from "../../components/MarketMonitor/loadZoneUtils";
import { getHeaders as getMarketDataHeaders } from "../../components/ApiHeaders/apiHeadersMarketdata";

const tabBaseClass =
  "px-3 sm:px-4 md:px-8 py-2 sm:py-3 md:py-[10px] text-sm sm:text-base md:text-[20px] rounded-t-[10px] transition-all";

const getTabClassName = (isActive: boolean): string =>
  `${tabBaseClass} ${
    isActive
      ? "bg-white text-[#FF2C2C] font-bold border-t-[2px] border-l-[2px] border-r-[2px] border-[#EAE7FF] mr-2 sm:mr-4 md:mr-[10px]"
      : "bg-[#e3e5f2] text-gray-600 mr-2 sm:mr-4 md:mr-[10px]"
  }`;

type MarketMonitorTab = "ISO_ACTUAL_AND_FORECAST" | "LOAD_ZONE";
type IsoCode = "CAISO" | "ERCOT" | "ISONE" | "NYISO" | "PJM";

const ISO_OPTIONS: Array<{ value: IsoCode; label: string; realtimePath: string }> = [
  { value: "CAISO", label: "CAISO", realtimePath: "/caiso/realtime" },
  { value: "ERCOT", label: "ERCOT", realtimePath: "/ercot/realtime" },
  { value: "ISONE", label: "ISONE", realtimePath: "/isone/realtime" },
  { value: "NYISO", label: "NYISO", realtimePath: "/nyiso/realtime" },
  { value: "PJM", label: "PJM", realtimePath: "/pjm/realtime" },
];

interface ApiResponse<T> {
  response?: {
    data?: T[];
  };
}

interface MarketDataRow {
  ISO?: string;
  OBJECTNAME?: string | null;
  DATATYPEID?: number | string | null;
  TIMESTAMP?: string;
  DATETIME?: string;
  DATA_POINTS?: string;
  SHORTNAME?: string;
  VALUE?: string | number | null;
  VOLUME?: string | number | null;
}

interface DatatypeRow {
  DATATYPEID?: number | string | null;
  GENERATIONTYPE?: string | null;
}

interface MarketMonitorDatasets {
  loadForecastRows: MarketDataRow[];
  renewableForecastRows: MarketDataRow[];
  realtimeLoadRows: MarketDataRow[];
  renewableRealtimeRows: MarketDataRow[];
  curtailmentRows: MarketDataRow[];
  datatypesRows: DatatypeRow[];
}

const MARKETDATA_API_BASE_URL = (process.env.REACT_APP_MARKETDATA_API ?? "").trim();

const joinUrl = (baseUrl: string, path: string): string => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
};

const fetchMarketDataEndpoint = async (
  path: string,
  headers: Record<string, string>
): Promise<ApiResponse<MarketDataRow>> => {
  if (!MARKETDATA_API_BASE_URL) {
    throw new Error("Missing marketdata base URL configuration");
  }

  const apiResponse = await axios.get<ApiResponse<MarketDataRow>>(
    joinUrl(MARKETDATA_API_BASE_URL, path),
    { headers }
  );

  return apiResponse.data;
};

const toErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const endpoint = error.config?.url ?? "unknown endpoint";
    const details =
      typeof error.response?.data === "string"
        ? error.response.data
        : JSON.stringify(error.response?.data ?? {});

    if (status === 401) {
      return `Market Fundamentals request unauthorized (401) at ${endpoint}. Verify token scope, subscription key, and product mapping. ${details}`;
    }

    if (!status) {
      const errorCode = error.code ? ` [${error.code}]` : "";
      return `Market Fundamentals request failed (network/client error) at ${endpoint}. ${error.message}${errorCode}`;
    }

    return `Market Fundamentals request failed (${status ?? "unknown status"}) at ${endpoint}. ${details}`;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Unknown error while loading Market Fundamentals data.";
};

const fetchDatatypes = async (headers: Record<string, string>): Promise<ApiResponse<DatatypeRow>> => {
  if (!MARKETDATA_API_BASE_URL) {
    throw new Error("Missing marketdata base URL configuration");
  }

  const apiResponse = await axios.get<ApiResponse<DatatypeRow>>(
    joinUrl(MARKETDATA_API_BASE_URL, "/market_monitor/datatypes"),
    { headers }
  );

  return apiResponse.data;
};

interface IsoActualAndForecastReportProps {
  datasets: MarketMonitorDatasets;
  selectedIso: IsoCode;
}

const IsoActualAndForecastReport: React.FC<IsoActualAndForecastReportProps> = ({
  datasets,
  selectedIso,
}) => {
  return <NetLoadVisual {...datasets} selectedIso={selectedIso} />;
};

interface LoadZoneReportProps {
  datasets: MarketMonitorDatasets;
  selectedIso: IsoCode;
  selectedObjectName: LoadZoneObjectName | null;
  onSelectObjectName: (name: LoadZoneObjectName) => void;
}

const LOAD_ZONE_MAX_POINTS = 14 * 24;

const toHourKey = (value: string | undefined): string | null => {
  if (!value) {
    return null;
  }

  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const parsed = new Date(normalized);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  parsed.setMinutes(0, 0, 0);
  return parsed.toISOString();
};

const LoadZoneReport: React.FC<LoadZoneReportProps> = ({
  datasets,
  selectedIso,
  selectedObjectName,
  onSelectObjectName,
}) => {
  const loadForecastTimeline = useMemo(() => {
    const keys = datasets.loadForecastRows
      .filter((row) => matchesSelectedIso(row.ISO, selectedIso) && row.DATA_POINTS === "LOAD_FORECAST")
      .map((row) => toHourKey(row.TIMESTAMP ?? row.DATETIME))
      .filter((key): key is string => key !== null);

    return Array.from(new Set(keys)).sort();
  }, [datasets.loadForecastRows, selectedIso]);

  const loadZoneWindow = useMemo(() => {
    if (!loadForecastTimeline.length) {
      return { start: null as string | null, end: null as string | null };
    }

    const endIndex = loadForecastTimeline.length - 1;
    const startIndex = Math.max(0, loadForecastTimeline.length - LOAD_ZONE_MAX_POINTS);

    return {
      start: loadForecastTimeline[startIndex],
      end: loadForecastTimeline[endIndex],
    };
  }, [loadForecastTimeline]);

  const clipRowsToLoadZoneWindow = useMemo(() => {
    if (!loadZoneWindow.start || !loadZoneWindow.end) {
      return {
        renewableForecastRows: datasets.renewableForecastRows,
        renewableRealtimeRows: datasets.renewableRealtimeRows,
      };
    }

    const isWithinWindow = (row: MarketDataRow): boolean => {
      const hourKey = toHourKey(row.TIMESTAMP ?? row.DATETIME);
      if (!hourKey) {
        return false;
      }

      return hourKey >= loadZoneWindow.start! && hourKey <= loadZoneWindow.end!;
    };

    return {
      renewableForecastRows: datasets.renewableForecastRows.filter(isWithinWindow),
      renewableRealtimeRows: datasets.renewableRealtimeRows.filter(isWithinWindow),
    };
  }, [datasets.renewableForecastRows, datasets.renewableRealtimeRows, loadZoneWindow]);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_1.45fr] gap-5 items-start xl:h-[980px]">
      <LoadZoneRenewableSummaryVisuals
        renewableForecastRows={clipRowsToLoadZoneWindow.renewableForecastRows}
        renewableRealtimeRows={clipRowsToLoadZoneWindow.renewableRealtimeRows}
        datatypesRows={datasets.datatypesRows}
        selectedIso={selectedIso}
        selectedObjectName={selectedObjectName}
      />

      <div className="space-y-5 xl:space-y-0 xl:flex xl:flex-col xl:gap-5 xl:h-full">
        <LoadZoneActualLoadVisual
          renewableRealtimeRows={clipRowsToLoadZoneWindow.renewableRealtimeRows}
          selectedIso={selectedIso}
          selectedObjectName={selectedObjectName}
          onSelectObjectName={onSelectObjectName}
        />
        <LoadZoneForecastVisual
          renewableForecastRows={clipRowsToLoadZoneWindow.renewableForecastRows}
          selectedIso={selectedIso}
          selectedObjectName={selectedObjectName}
          onSelectObjectName={onSelectObjectName}
        />
        <LoadZoneGenerationSpreadVisual
          renewableRealtimeRows={clipRowsToLoadZoneWindow.renewableRealtimeRows}
          renewableForecastRows={clipRowsToLoadZoneWindow.renewableForecastRows}
          datatypesRows={datasets.datatypesRows}
          selectedIso={selectedIso}
          selectedObjectName={selectedObjectName}
          onSelectObjectName={onSelectObjectName}
        />
      </div>
    </div>
  );
};

const MarketMonitorVisualPage: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState<MarketMonitorTab>("ISO_ACTUAL_AND_FORECAST");
  const [selectedIso, setSelectedIso] = useState<IsoCode>("CAISO");
  const [isIsoDropdownOpen, setIsIsoDropdownOpen] = useState<boolean>(false);
  const [selectedLoadZoneObjectName, setSelectedLoadZoneObjectName] =
    useState<LoadZoneObjectName | null>(null);
  const isoDropdownRef = useRef<HTMLDivElement | null>(null);
  const [datasets, setDatasets] = useState<MarketMonitorDatasets>({
    loadForecastRows: [],
    renewableForecastRows: [],
    realtimeLoadRows: [],
    renewableRealtimeRows: [],
    curtailmentRows: [],
    datatypesRows: [],
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const fetchAllDatasets = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const headers = await getMarketDataHeaders();

        const [
          loadForecastRes,
          renewableForecastRes,
          renewableRealtimeRes,
          curtailmentRes,
          datatypesRes,
          realtimeResults,
        ] = await Promise.all([
          fetchMarketDataEndpoint("/market_monitor/load_forecast", headers),
          fetchMarketDataEndpoint("/market_monitor/renewable_gen_forecast", headers),
          fetchMarketDataEndpoint("/market_monitor/renewable_gen_realtime", headers),
          fetchMarketDataEndpoint("/market_monitor/curtailment", headers),
          fetchDatatypes(headers),
          Promise.allSettled(
            ISO_OPTIONS.map((iso) => fetchMarketDataEndpoint(iso.realtimePath, headers))
          ),
        ]);

        if (!active) {
          return;
        }

        const mergedRealtimeRows = realtimeResults
          .filter(
            (result): result is PromiseFulfilledResult<ApiResponse<MarketDataRow>> =>
              result.status === "fulfilled"
          )
          .flatMap((result) => result.value.response?.data ?? []);

        const failedRealtimePaths = realtimeResults
          .map((result, index) => ({ result, path: ISO_OPTIONS[index].realtimePath }))
          .filter((item) => item.result.status === "rejected")
          .map((item) => item.path);

        if (failedRealtimePaths.length > 0) {
          console.warn("Market Fundamentals realtime endpoints failed", failedRealtimePaths);
        }

        setDatasets({
          loadForecastRows: loadForecastRes.response?.data ?? [],
          renewableForecastRows: renewableForecastRes.response?.data ?? [],
          realtimeLoadRows: mergedRealtimeRows,
          renewableRealtimeRows: renewableRealtimeRes.response?.data ?? [],
          curtailmentRows: curtailmentRes.response?.data ?? [],
          datatypesRows: datatypesRes.response?.data ?? [],
        });
      } catch (fetchError) {
        console.error("Market Fundamentals parent data fetch failed", fetchError);
        if (!active) {
          return;
        }

        setLoadError(toErrorMessage(fetchError));
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    fetchAllDatasets();

    return () => {
      active = false;
    };
  }, []);

  const handleLoadZoneObjectSelection = (name: LoadZoneObjectName) => {
    setSelectedLoadZoneObjectName((previous) => (previous === name ? null : name));
  };

  const selectedIsoLabel = ISO_OPTIONS.find((iso) => iso.value === selectedIso)?.label ?? selectedIso;

  const handleIsoSelect = (iso: IsoCode) => {
    setSelectedIso(iso);
    setIsIsoDropdownOpen(false);
  };

  useEffect(() => {
    setSelectedLoadZoneObjectName(null);
  }, [selectedIso]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!isoDropdownRef.current) {
        return;
      }

      if (!isoDropdownRef.current.contains(event.target as Node)) {
        setIsIsoDropdownOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsIsoDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-[#F7F7FF] xl:h-screen xl:overflow-hidden">
      <div className="flex-grow xl:flex xl:min-h-0 xl:flex-col">
        <div className="xl:shrink-0">
          <Header />
        </div>

        <main className="mx-10 mt-10 mb-8 font-poppins xl:flex xl:flex-1 xl:min-h-0 xl:flex-col">
          <div className="flex items-center justify-between mb-6 xl:shrink-0">
            <h1 className="text-2xl font-bold text-gray-700">Market Fundamentals</h1>
            <div className="text-gray-600">
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </div>
          </div>

          <div className="mb-5 xl:flex xl:flex-1 xl:min-h-0 xl:flex-col">
            <div className="flex flex-wrap items-center justify-between gap-3 -mb-[2px] px-2 md:px-3 xl:shrink-0">
              <div className="flex justify-start bg-[#F7F7FF] flex-wrap">
                <button
                  type="button"
                  className={getTabClassName(selectedTab === "ISO_ACTUAL_AND_FORECAST")}
                  onClick={() => setSelectedTab("ISO_ACTUAL_AND_FORECAST")}
                >
                  ISO Actual and Forecast
                </button>
                <button
                  type="button"
                  className={getTabClassName(selectedTab === "LOAD_ZONE")}
                  onClick={() => setSelectedTab("LOAD_ZONE")}
                >
                  Load Zone
                </button>
              </div>

              <div className="flex items-center gap-2 rounded-xl border border-[#D6DAEB] bg-white px-3 py-2 mb-[2px] transition-all duration-200 focus-within:border-[#BCC5F0] focus-within:ring-2 focus-within:ring-[#EAE7FF]">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  ISO
                </span>
                <div className="relative" ref={isoDropdownRef}>
                  <button
                    id="market-monitor-iso-select"
                    type="button"
                    aria-haspopup="listbox"
                    aria-expanded={isIsoDropdownOpen}
                    onClick={() => setIsIsoDropdownOpen((previous) => !previous)}
                    className="flex min-w-[124px] items-center justify-between gap-2 rounded-md px-1.5 py-0.5 text-sm font-semibold text-gray-700 outline-none transition-colors duration-150 hover:bg-[#F5F7FF]"
                  >
                    <span>{selectedIsoLabel}</span>
                    <svg
                      className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                        isIsoDropdownOpen ? "rotate-180" : "rotate-0"
                      }`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" />
                    </svg>
                  </button>

                  {isIsoDropdownOpen && (
                    <div className="absolute right-0 top-full z-30 mt-2 w-[160px] overflow-hidden rounded-xl border border-[#D6DAEB] bg-white shadow-[0_10px_24px_rgba(31,42,153,0.16)]">
                      <ul role="listbox" aria-labelledby="market-monitor-iso-select" className="py-1">
                        {ISO_OPTIONS.map((iso) => {
                          const isSelected = iso.value === selectedIso;

                          return (
                            <li key={iso.value}>
                              <button
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                onClick={() => handleIsoSelect(iso.value)}
                                className={`flex w-full items-center justify-between px-3 py-2 text-sm transition-colors duration-150 ${
                                  isSelected
                                    ? "bg-[#EFF2FF] font-semibold text-[#1F2A99]"
                                    : "text-gray-700 hover:bg-[#F5F7FF]"
                                }`}
                              >
                                <span>{iso.label}</span>
                                {isSelected && (
                                  <svg
                                    className="h-4 w-4 text-[#1F2A99]"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.704 5.29a1 1 0 010 1.414l-8.02 8.02a1 1 0 01-1.414 0L3.296 10.75a1 1 0 111.414-1.414l3.267 3.267 7.313-7.313a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="w-full rounded-[15px] border-[2px] border-[#EAE7FF] bg-[#F7F7FF] px-3 pb-3 pt-3 md:px-4 md:pb-4 md:pt-3 xl:flex xl:flex-1 xl:min-h-0 xl:overflow-y-auto">
              {isLoading ? (
                <div className="w-full h-[560px] rounded-xl bg-white p-4 flex items-center justify-center text-gray-500">
                  Loading Market Fundamentals data...
                </div>
              ) : loadError ? (
                <div className="w-full h-[560px] rounded-xl bg-white p-4 flex items-center justify-center text-red-500">
                  {loadError}
                </div>
              ) : selectedTab === "ISO_ACTUAL_AND_FORECAST" ? (
                <IsoActualAndForecastReport datasets={datasets} selectedIso={selectedIso} />
              ) : (
                <LoadZoneReport
                  datasets={datasets}
                  selectedIso={selectedIso}
                  selectedObjectName={selectedLoadZoneObjectName}
                  onSelectObjectName={handleLoadZoneObjectSelection}
                />
              )}
            </div>
          </div>
        </main>
      </div>

      <Footer />
    </div>
  );
};

export default MarketMonitorVisualPage;
